import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { JmapClient } from './lib/jmap.js';
import { CryptoClient } from './lib/crypto.js';
import { createApis, recoveryApiForAuth } from './lib/api.js';
import { useTheme } from './lib/theme.js';
import { useLocalKeystore } from './lib/keystore.js';
import { ToastProvider, useToast } from './components/ui.jsx';
import { AuthShell } from './pages/AuthShell.jsx';
import { Login } from './pages/Login.jsx';
import { Register } from './pages/Register.jsx';
import { Forgot } from './pages/Forgot.jsx';
import { FindAddress } from './pages/FindAddress.jsx';
import { Shell } from './pages/Shell.jsx';

// Root state machine: login | register | forgot | find | app.
export function App() {
  return <ToastProvider><Root /></ToastProvider>;
}

function Root() {
  const theme = useTheme('light');
  const flash = useToast();
  const keystore = useLocalKeystore();
  const [screen, setScreen] = useState({ name: 'login', email: '' });
  const [session, setSession] = useState(null); // { jmap, crypto, email, apis }
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);
  const recoveryApi = useMemo(() => recoveryApiForAuth(), []);

  useEffect(() => { recoveryApi.registration().then(setRegistration).catch(() => setRegistration({ enabled: false })); }, [recoveryApi]);

  const go = useCallback((name, extra = {}) => { setError(null); setScreen({ name, ...extra }); }, []);

  const handleLogin = useCallback(async ({ email, password }) => {
    setError(null);
    try {
      const jmap = new JmapClient({ baseUrl: '', authHeader: JmapClient.basicAuth(email, password) });
      await jmap.connect();
      const crypto = new CryptoClient();
      const apis = createApis({ jmap, authHeader: jmap.authHeader });
      setSession({ jmap, crypto, email, apis, keystore });
      flash('Keys unlocked locally');
    } catch (e) {
      setError(e.message || 'Login failed');
    }
  }, [flash, keystore]);

  const handleRegistered = useCallback(async ({ email, password, phrase }) => {
    await keystore.create(email, { password, phrase });
    await handleLogin({ email, password });
  }, [handleLogin, keystore]);

  const handleLogout = useCallback(() => { setSession(null); setScreen({ name: 'login', email: '' }); }, []);

  if (session) return <Shell session={session} theme={theme} onLogout={handleLogout} />;

  return (
    <AuthShell theme={theme}>
      {screen.name === 'login' && <Login onLogin={handleLogin} error={error} initialEmail={screen.email} go={go} onPasskey={() => flash('Passkey sign-in: wire to WebAuthn')} />}
      {screen.name === 'register' && <Register api={recoveryApi} crypto={new CryptoClient()} registration={registration} onDone={handleRegistered} go={go} />}
      {screen.name === 'forgot' && <Forgot api={recoveryApi} initialEmail={screen.email} go={go} />}
      {screen.name === 'find' && <FindAddress api={recoveryApi} go={go} />}
    </AuthShell>
  );
}
